import type { AnnotationAction, PublicAnnotation, PublicReviewer, ReviewAsset } from '../../../shared/types';

export const reviewPrimaryButton =
  'app-button inline-flex min-h-[46px] items-center justify-center gap-2 rounded-full border border-accent bg-accent px-5 py-2.5 text-sm font-semibold text-white transition-all duration-300 hover:border-accent-hover hover:bg-accent-hover focus-visible:outline focus-visible:outline-[2.5px] focus-visible:outline-offset-3 focus-visible:outline-accent disabled:cursor-wait disabled:opacity-60 disabled:hover:translate-y-0';
export const reviewPrimaryGlowButton = `${reviewPrimaryButton} app-button-glow`;
export const reviewSecondaryButton =
  'app-button inline-flex min-h-[46px] items-center justify-center gap-2 rounded-full border border-line bg-transparent px-5 py-2.5 text-sm font-semibold text-fg transition-all duration-300 hover:border-fg/60 hover:bg-elevated focus-visible:outline focus-visible:outline-[2.5px] focus-visible:outline-offset-3 focus-visible:outline-accent disabled:cursor-wait disabled:opacity-60 disabled:hover:translate-y-0';
export const reviewQuietButton =
  'app-button inline-flex min-h-11 items-center justify-center gap-2 rounded-full border border-transparent bg-transparent px-4 py-2 text-sm font-semibold text-muted transition-all duration-300 hover:text-fg focus-visible:outline focus-visible:outline-[2.5px] focus-visible:outline-offset-3 focus-visible:outline-accent disabled:cursor-wait disabled:opacity-60 disabled:hover:translate-y-0';
export const reviewTextButton =
  'app-button rounded-full px-0.5 font-semibold text-accent underline-offset-4 transition-colors duration-200 hover:text-accent-hover hover:underline focus-visible:outline focus-visible:outline-[2.5px] focus-visible:outline-offset-3 focus-visible:outline-accent';
export const reviewInput =
  'mt-2 min-h-12 w-full rounded-input border border-line bg-card px-3.5 py-2.5 text-fg outline-none transition focus:border-accent focus:ring-3 focus:ring-accent/10';
export const reviewCard = 'rounded-card border border-line bg-card shadow-card';

export const actionLabels: Record<AnnotationAction, string> = {
  use: 'Use',
  maybe: 'Maybe',
  remove: 'Remove',
  comment: 'Comment',
};

export const actionTone: Record<AnnotationAction, string> = {
  use: 'border-success/30 bg-success-tint text-success',
  maybe: 'border-warning/30 bg-warning-tint text-warning',
  remove: 'border-error/30 bg-error-tint text-error',
  comment: 'border-accent/25 bg-accent-tint text-accent',
};

export const actionRail: Record<AnnotationAction, string> = {
  use: '#2a6f50',
  maybe: '#8a6410',
  remove: '#a23b2e',
  comment: '#14564b',
};

export function formatReviewTime(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) return '00:00';
  const whole = Math.max(0, Math.floor(seconds));
  const hours = Math.floor(whole / 3600);
  const minutes = Math.floor((whole % 3600) / 60);
  const remaining = whole % 60;
  return hours > 0
    ? `${hours}:${String(minutes).padStart(2, '0')}:${String(remaining).padStart(2, '0')}`
    : `${String(minutes).padStart(2, '0')}:${String(remaining).padStart(2, '0')}`;
}

export function reviewerInitials(reviewer: PublicReviewer | undefined): string {
  if (!reviewer) return '?';
  return reviewer.display_name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]?.toLocaleUpperCase('en-AU'))
    .join('');
}

export function nextReviewEntryState(reviewer: PublicReviewer | null): 'identity' | 'ready' {
  return reviewer ? 'ready' : 'identity';
}

export function reviewerAuthorLabel(reviewer: PublicReviewer | undefined): string {
  return reviewer?.is_owner ? 'Sheahan Studios' : reviewer?.display_name ?? 'Reviewer';
}

export type ReviewPrompt =
  | { type: 'interview'; assetId: string; title: string }
  | { type: 'selects' };

export function reviewedInterviewIds(
  annotations: Array<Pick<PublicAnnotation, 'asset_id' | 'reviewer_id'>>,
  reviewerId: string | null,
): Set<string> {
  if (!reviewerId) return new Set();
  return new Set(
    annotations
      .filter((annotation) => annotation.reviewer_id === reviewerId)
      .map((annotation) => annotation.asset_id),
  );
}

export function nextReviewPrompt(
  assets: Array<Pick<ReviewAsset, 'id' | 'title'>>,
  activeAssetId: string | null,
  reviewedIds: ReadonlySet<string>,
): ReviewPrompt | null {
  const activeIndex = assets.findIndex((asset) => asset.id === activeAssetId);
  if (activeIndex < 0 || !activeAssetId || !reviewedIds.has(activeAssetId)) return null;

  const remaining = [
    ...assets.slice(activeIndex + 1),
    ...assets.slice(0, activeIndex),
  ].find((asset) => !reviewedIds.has(asset.id));
  if (remaining) return { type: 'interview', assetId: remaining.id, title: remaining.title };
  return assets.length > 0 ? { type: 'selects' } : null;
}

const REVIEW_PROMPT_DISMISSED_PREFIX = 'ss_review_prompt_dismissed:';

export function reviewPromptDismissalKey(projectId: string): string {
  return `${REVIEW_PROMPT_DISMISSED_PREFIX}${projectId}`;
}

/** localStorage throws outright in some privacy modes; session storage still nudges once per tab. */
function reviewStorage(): Storage | null {
  try {
    return localStorage;
  } catch {
    try {
      return sessionStorage;
    } catch {
      return null;
    }
  }
}

export function reviewPromptDismissed(projectId: string): boolean {
  try {
    return reviewStorage()?.getItem(reviewPromptDismissalKey(projectId)) === '1';
  } catch {
    return false;
  }
}

export function dismissReviewPrompt(projectId: string): void {
  try {
    reviewStorage()?.setItem(reviewPromptDismissalKey(projectId), '1');
  } catch {
    // The card should still close for this session when storage is unavailable.
  }
}

export function DecisionIcon({ action, className = 'h-4 w-4' }: { action: AnnotationAction; className?: string }) {
  if (action === 'use') {
    return <svg className={className} viewBox="0 0 20 20" aria-hidden="true"><path d="m4 10 3.5 3.5L16 5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>;
  }
  if (action === 'maybe') {
    return <svg className={className} viewBox="0 0 20 20" aria-hidden="true"><path d="M7.5 7a2.7 2.7 0 1 1 4.1 2.3c-1 .6-1.6 1.1-1.6 2.2" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /><circle cx="10" cy="15" r="1" fill="currentColor" /></svg>;
  }
  if (action === 'remove') {
    return <svg className={className} viewBox="0 0 20 20" aria-hidden="true"><path d="M5 5l10 10M15 5 5 15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>;
  }
  return <svg className={className} viewBox="0 0 20 20" aria-hidden="true"><path d="M3.5 4.5h13v9h-7l-3.5 3v-3H3.5z" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" /></svg>;
}

export function ContactLine() {
  return <p className="mt-6 text-sm text-faint">Questions? Reply to Henry&apos;s email or call.</p>;
}
